/**
 * A strict markdown subset parser.
 *
 * Duplicated verbatim from client-dashboard/src/lib/markdown/parse.ts. No
 * shared package by design; the three apps stay in sync by review.
 *
 * The threat this exists for: the agent has just read an attacker-controllable
 * repository. Its output reaches this tab, where the access token lives in
 * memory - exactly as exfiltratable as the localStorage the design rejected. A
 * prompt-injected repo that gets HTML through this path achieves XSS.
 *
 * Being inside the trust boundary changes NOTHING here. The expert reads more
 * of the agent's output than the client does, not less, so this surface has
 * strictly more untrusted markdown crossing it - and an expert's token can
 * claim chats, prompt agents and promote to production.
 *
 * Two structural decisions make that impossible rather than unlikely:
 *
 *  1. **This parser never produces HTML.** It produces a small, closed node
 *     union that the renderer turns into React elements. There is no HTML
 *     string anywhere in the pipeline, therefore no `dangerouslySetInnerHTML`,
 *     therefore no sanitizer to get wrong. Raw HTML in the source is emitted as
 *     literal text.
 *  2. **The node union is an allowlist, not a denylist.** Anything the grammar
 *     below does not explicitly build cannot exist downstream. Adding a feature
 *     is a deliberate edit here, and the type system makes every renderer
 *     handle it.
 *
 * Link schemes are checked in `sanitizeUrl` and images are dropped entirely -
 * see the comments there.
 */

export type InlineNode =
  | { type: 'text'; value: string }
  | { type: 'strong'; children: InlineNode[] }
  | { type: 'em'; children: InlineNode[] }
  | { type: 'del'; children: InlineNode[] }
  | { type: 'code'; value: string }
  | { type: 'link'; href: string; children: InlineNode[] }
  /** An image or a link whose scheme failed the allowlist. Rendered as inert text. */
  | { type: 'blocked'; label: string; reason: 'scheme' | 'image' }
  | { type: 'break' };

export type BlockNode =
  | { type: 'paragraph'; children: InlineNode[] }
  | { type: 'heading'; level: 1 | 2 | 3; children: InlineNode[] }
  | { type: 'list'; ordered: boolean; start: number; items: BlockNode[][] }
  | { type: 'quote'; children: BlockNode[] }
  | { type: 'codeBlock'; value: string; lang: string | null }
  | { type: 'rule' };

const LF = String.fromCharCode(10);

/**
 * URL allowlist.
 *
 * `https` and `mailto` only.
 *
 * `http` is excluded because every surface this product links to is TLS, so a
 * plaintext link is either a downgrade or a beacon. `data:`, `javascript:`,
 * `vbscript:`, `blob:` and `file:` are the classic injection vectors. Anything
 * unrecognised is refused rather than guessed at.
 *
 * Protocol-relative (`//host`) and relative URLs are refused too: the agent has
 * no business linking inside this dashboard, and a relative link is an easy way
 * to make a hostile target look local.
 */
export function sanitizeUrl(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;

  // Strip control characters before parsing. `java\nscript:` is a real bypass
  // against parsers that normalise after the scheme check.
  let cleaned = '';
  for (const ch of trimmed) {
    const code = ch.codePointAt(0) ?? 0;
    if (code < 0x20 || code === 0x7f) continue;
    cleaned += ch;
  }
  if (!cleaned) return null;
  if (cleaned.startsWith('//')) return null;

  let url: URL;
  try {
    url = new URL(cleaned);
  } catch {
    return null; // relative or malformed - refused
  }

  if (url.protocol === 'https:' || url.protocol === 'mailto:') return url.toString();
  return null;
}

// ---------------------------------------------------------------------------
// Inline
// ---------------------------------------------------------------------------

function pushText(nodes: InlineNode[], value: string): void {
  if (!value) return;
  const last = nodes[nodes.length - 1];
  if (last && last.type === 'text') last.value += value;
  else nodes.push({ type: 'text', value });
}

function parseInline(src: string, depth = 0): InlineNode[] {
  const nodes: InlineNode[] = [];
  if (depth > 6) {
    pushText(nodes, src);
    return nodes;
  }

  let i = 0;
  let buffer = '';

  const flush = () => {
    pushText(nodes, buffer);
    buffer = '';
  };

  while (i < src.length) {
    const ch = src[i]!;

    // Backslash escape - the next character is always literal.
    if (ch === '\\' && i + 1 < src.length) {
      buffer += src[i + 1];
      i += 2;
      continue;
    }

    // Inline code. Highest precedence: nothing inside a code span is markup.
    if (ch === '`') {
      let fence = 0;
      while (src[i + fence] === '`') fence += 1;
      const marker = '`'.repeat(fence);
      const close = src.indexOf(marker, i + fence);
      if (close !== -1) {
        flush();
        nodes.push({ type: 'code', value: src.slice(i + fence, close) });
        i = close + fence;
        continue;
      }
      buffer += marker;
      i += fence;
      continue;
    }

    // Image: `![alt](src)`. Dropped, alt kept.
    //
    // A remote image is a tracking pixel and an IP disclosure at minimum, and
    // the agent read an untrusted repo to find that URL. There is no version of
    // this product where a client needs the agent to show them a picture from
    // the internet, so the whole class goes away.
    if (ch === '!' && src[i + 1] === '[') {
      const parsed = parseLinkLike(src, i + 1);
      if (parsed) {
        flush();
        nodes.push({ type: 'blocked', label: parsed.label || 'image', reason: 'image' });
        i = parsed.end;
        continue;
      }
    }

    // Link: `[text](href)`
    if (ch === '[') {
      const parsed = parseLinkLike(src, i);
      if (parsed) {
        const href = sanitizeUrl(parsed.href);
        flush();
        if (href) {
          nodes.push({ type: 'link', href, children: parseInline(parsed.label, depth + 1) });
        } else {
          nodes.push({ type: 'blocked', label: parsed.label || parsed.href, reason: 'scheme' });
        }
        i = parsed.end;
        continue;
      }
    }

    // Strong / emphasis / strikethrough.
    const emphasis =
      matchDelimiter(src, i, '***', depth) ??
      matchDelimiter(src, i, '**', depth) ??
      matchDelimiter(src, i, '~~', depth) ??
      matchDelimiter(src, i, '*', depth) ??
      matchDelimiter(src, i, '__', depth) ??
      matchDelimiter(src, i, '_', depth);
    if (emphasis) {
      flush();
      nodes.push(emphasis.node);
      i = emphasis.end;
      continue;
    }

    buffer += ch;
    i += 1;
  }

  flush();
  return nodes;
}

function matchDelimiter(
  src: string,
  start: number,
  marker: string,
  depth: number,
): { node: InlineNode; end: number } | null {
  if (!src.startsWith(marker, start)) return null;
  const contentStart = start + marker.length;
  if (src[contentStart] === ' ' || contentStart >= src.length) return null;

  let search = contentStart;
  for (;;) {
    const close = src.indexOf(marker, search);
    if (close === -1) return null;
    if (src[close - 1] === '\\') {
      search = close + marker.length;
      continue;
    }
    const inner = src.slice(contentStart, close);
    if (!inner) return null;
    const children = parseInline(inner, depth + 1);
    const end = close + marker.length;

    if (marker === '***') {
      return { node: { type: 'strong', children: [{ type: 'em', children }] }, end };
    }
    if (marker === '**' || marker === '__') return { node: { type: 'strong', children }, end };
    if (marker === '~~') return { node: { type: 'del', children }, end };
    return { node: { type: 'em', children }, end };
  }
}

/** Parses `[label](target)` starting at the `[`. Handles one level of nesting. */
function parseLinkLike(src: string, start: number): { label: string; href: string; end: number } | null {
  if (src[start] !== '[') return null;
  let depth = 0;
  let i = start;
  let labelEnd = -1;
  for (; i < src.length; i += 1) {
    const c = src[i];
    if (c === '\\') {
      i += 1;
      continue;
    }
    if (c === '[') depth += 1;
    else if (c === ']') {
      depth -= 1;
      if (depth === 0) {
        labelEnd = i;
        break;
      }
    }
  }
  if (labelEnd === -1 || src[labelEnd + 1] !== '(') return null;

  const close = src.indexOf(')', labelEnd + 2);
  if (close === -1) return null;

  const target = src.slice(labelEnd + 2, close).trim();
  // Drop a markdown title: `(https://x "Title")`
  const href = target.split(/\s+/)[0] ?? '';

  return { label: src.slice(start + 1, labelEnd), href, end: close + 1 };
}

// ---------------------------------------------------------------------------
// Block
// ---------------------------------------------------------------------------

const HEADING_RE = /^(#{1,3})\s+(.*)$/;
const UL_RE = /^[-*+]\s+(.*)$/;
const OL_RE = /^(\d{1,9})[.)]\s+(.*)$/;
const RULE_RE = /^([-*_])\s*(\1\s*){2,}$/;
const FENCE_RE = /^(`{3,}|~{3,})\s*([A-Za-z0-9_+-]{0,24}).*$/;

export function parseMarkdown(source: string): BlockNode[] {
  const lines = source.replace(new RegExp(String.fromCharCode(13), 'g'), '').split(LF);
  return parseBlocks(lines);
}

function parseBlocks(lines: string[]): BlockNode[] {
  const blocks: BlockNode[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i]!;

    if (!line.trim()) {
      i += 1;
      continue;
    }

    // Fenced code. Contents are never parsed - that is the point of a fence.
    const fence = FENCE_RE.exec(line);
    if (fence) {
      const marker = fence[1]!;
      const lang = fence[2] ? fence[2] : null;
      const body: string[] = [];
      i += 1;
      while (i < lines.length && !lines[i]!.trimEnd().startsWith(marker)) {
        body.push(lines[i]!);
        i += 1;
      }
      i += 1; // consume the closing fence (or run off the end)
      blocks.push({ type: 'codeBlock', value: body.join(LF), lang });
      continue;
    }

    if (RULE_RE.test(line.trim())) {
      blocks.push({ type: 'rule' });
      i += 1;
      continue;
    }

    const heading = HEADING_RE.exec(line);
    if (heading) {
      const level = Math.min(3, heading[1]!.length) as 1 | 2 | 3;
      blocks.push({ type: 'heading', level, children: parseInline(heading[2]!.trim()) });
      i += 1;
      continue;
    }

    // Blockquote: gather the contiguous `>` run and parse it as blocks.
    if (line.trimStart().startsWith('>')) {
      const inner: string[] = [];
      while (i < lines.length && lines[i]!.trimStart().startsWith('>')) {
        inner.push(lines[i]!.trimStart().replace(/^>\s?/, ''));
        i += 1;
      }
      blocks.push({ type: 'quote', children: parseBlocks(inner) });
      continue;
    }

    // Lists.
    const ul = UL_RE.exec(line.trimStart());
    const ol = OL_RE.exec(line.trimStart());
    if (ul || ol) {
      const ordered = Boolean(ol);
      const start = ol ? Number.parseInt(ol[1]!, 10) : 1;
      const items: BlockNode[][] = [];
      let current: string[] | null = null;

      while (i < lines.length) {
        const raw = lines[i]!;
        const trimmed = raw.trimStart();
        const itemUl = UL_RE.exec(trimmed);
        const itemOl = OL_RE.exec(trimmed);
        const isItem = ordered ? Boolean(itemOl) : Boolean(itemUl);

        if (isItem) {
          if (current) items.push(parseBlocks(current));
          current = [ordered ? itemOl![2]! : itemUl![1]!];
          i += 1;
          continue;
        }
        // A continuation line: indented, or a lazy paragraph line.
        if (trimmed && current && (raw.startsWith('  ') || raw.startsWith('\t'))) {
          current.push(trimmed);
          i += 1;
          continue;
        }
        break;
      }
      if (current) items.push(parseBlocks(current));
      blocks.push({ type: 'list', ordered, start: Number.isFinite(start) ? start : 1, items });
      continue;
    }

    // Paragraph: consume until a blank line or a line that starts another block.
    const para: string[] = [];
    while (i < lines.length) {
      const raw = lines[i]!;
      if (!raw.trim()) break;
      const t = raw.trimStart();
      if (
        HEADING_RE.test(raw) ||
        FENCE_RE.test(raw) ||
        RULE_RE.test(t) ||
        t.startsWith('>') ||
        UL_RE.test(t) ||
        OL_RE.test(t)
      ) {
        break;
      }
      para.push(raw.trim());
      i += 1;
    }
    if (para.length) {
      const children: InlineNode[] = [];
      para.forEach((text, index) => {
        if (index > 0) children.push({ type: 'break' });
        children.push(...parseInline(text));
      });
      blocks.push({ type: 'paragraph', children });
    }
  }

  return blocks;
}
